import { useEffect } from "react";

/**
 * Lightweight per-page SEO hook (no extra dependency).
 * Sets <title>, <meta name="description">, <meta name="keywords">,
 * <link rel="canonical">, OG/Twitter tags, and an optional JSON-LD blob.
 *
 * Usage:
 *   useSEO({
 *     title: "...",
 *     description: "...",
 *     keywords: "...",
 *     canonical: "/path",
 *     jsonLd: { "@context": "https://schema.org", "@type": "...", ... },
 *   });
 */
const SITE_ORIGIN = "https://www.svmd.in";

function setMetaByName(name, content) {
  if (!content) return;
  let el = document.querySelector(`meta[name="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("name", name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setMetaByProperty(property, content) {
  if (!content) return;
  let el = document.querySelector(`meta[property="${property}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("property", property);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setCanonical(href) {
  if (!href) return;
  let el = document.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

function setJsonLd(id, data) {
  // Remove previous instance with same id
  const existing = document.getElementById(id);
  if (existing) existing.remove();
  if (!data) return;
  const s = document.createElement("script");
  s.type = "application/ld+json";
  s.id = id;
  s.text = JSON.stringify(data);
  document.head.appendChild(s);
}

export default function useSEO({
  title,
  description,
  keywords,
  canonical,
  ogImage = "/about-hero.jpg",
  jsonLd,
} = {}) {
  useEffect(() => {
    if (title) document.title = title;

    setMetaByName("description", description);
    setMetaByName("keywords", keywords);

    const url = canonical
      ? canonical.startsWith("http")
        ? canonical
        : `${SITE_ORIGIN}${canonical}`
      : null;
    setCanonical(url);

    setMetaByProperty("og:title", title);
    setMetaByProperty("og:description", description);
    if (url) setMetaByProperty("og:url", url);
    if (ogImage) {
      const img = ogImage.startsWith("http") ? ogImage : `${SITE_ORIGIN}${ogImage}`;
      setMetaByProperty("og:image", img);
      setMetaByName("twitter:image", img);
    }
    setMetaByName("twitter:title", title);
    setMetaByName("twitter:description", description);

    setJsonLd("page-jsonld", jsonLd);
  }, [title, description, keywords, canonical, ogImage, jsonLd]);
}
